import { Percent } from "lucide-react"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { Input } from "../../components/ui/input"
import { cn, parseAttributes } from "../../lib/utils"
import {
	NumberInputConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useFormStore } from "@/stores/use-form-store"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const Number = ({ form, field, commonAttributes }: FieldProps) => {
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes
	const { placeholder, units } = getNumberInputAttributes(field.attributes)
	const { shouldDisable, verifiedFields } = useFormStore()
	const { hiddenFields, errors } = useLogicStore()

	const isVerified = verifiedFields.get(field.field_id) || false
	const isHidden = hiddenFields[field.field_id]

	if (isHidden) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
                    <FormControl>
                        {/* 06 */}
						<div className="relative">
							<Input
								disabled={isVerified || shouldDisable}
								{...formField}
								value={
									formField.value === "" ? 0 : formField.value
								}
								onChange={e => {
									const value = e.target.value // 0
									if (formField.value === "") {
										// remove leading zero (if that’s what you meant)
										const newValue = value.replace(
											/^0/,
											"",
										)
										formField.onChange(newValue)
										return
									}
									formField.onChange(value)
								}}
								placeholder={placeholder}
							/>
							{units && units === "percentage" && (
								<Percent className="absolute bottom-2 right-2" />
							)}
						</div>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getNumberInputAttributes(attributes: Attribute[]) {
	const props: {
		minValue?: number
		maxValue?: number
		decimalAllowed?: boolean
		units?: string
		placeholder?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)
	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case NumberInputConfig.Units:
				props.units = value as string
				break
			case NumberInputConfig.PlaceholderText:
				props.placeholder = value as string
		}
	})

	return props
}
